import { useMemo } from "react";

interface UsePaginationProps {
  count: number;
  itemsPerPage: number;
  siblingCount?: number; // Optional, defaults to 1
  page: number;
}

export const usePagination = ({
  count,
  itemsPerPage,
  siblingCount = 1,
  page,
}: UsePaginationProps) => {
  const paginationRange = useMemo(() => {
    const totalPageCount = Math.ceil(count / itemsPerPage);

    // Calculate sibling range and boundaries
    const totalPageNumbers = siblingCount + 5; // Includes first, last, and next/prev buttons

    if (totalPageNumbers >= totalPageCount) {
      return Array.from({ length: totalPageCount }, (_, i) => i + 1);
    }

    const leftSiblingIndex = Math.max(page - siblingCount, 1);
    const rightSiblingIndex = Math.min(page + siblingCount, totalPageCount);

    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2;

    const firstPageIndex = 1;
    const lastPageIndex = totalPageCount;

    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftRange = Array.from(
        { length: 3 + 2 * siblingCount },
        (_, i) => i + 1
      );
      return [...leftRange, "...", lastPageIndex];
    } else if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightRange = Array.from(
        { length: 3 + 2 * siblingCount },
        (_, i) => totalPageCount - (3 + 2 * siblingCount) + i + 1
      );
      return [firstPageIndex, "...", ...rightRange];
    } else if (shouldShowLeftDots && shouldShowRightDots) {
      const middleRange = Array.from(
        { length: 2 * siblingCount + 1 },
        (_, i) => leftSiblingIndex + i
      );
      return [firstPageIndex, "...", ...middleRange, "...", lastPageIndex];
    } else {
      return [];
    }
  }, [count, itemsPerPage, siblingCount, page]);

  const startItem = Math.min((page - 1) * itemsPerPage + 1, count);
  const endItem = Math.min(page * itemsPerPage, count);
  const displayText = `Showing ${startItem} to ${endItem} of ${count} results`;

  return {
    paginationRange,
    displayText,
    startItem,
    endItem,
  };
};
